import * as THREE from 'three'

import { MATERIAL_TILE_STUDS } from './materialTextures'

import { RobloxMaterial, Vec3 } from '../types'

/** スタッドテクスチャの 1 タイル解像度（px）。procedural 生成のため軽め。 */
const STUD_TEXTURE_SIZE = 64

/**
 * Roblox 風マテリアル質感マップ。
 * - emissiveFollowsColor=true（Neon）は applyMaterialState で state.color を反映する。
 * - それ以外は emissive=黒・emissiveIntensity=0 で発光なし。
 */
interface MaterialSpec {
  readonly roughness: number
  readonly metalness: number
  readonly emissiveFollowsColor: boolean
  /**
   * 固定色の弱い発光（CrackedLava の溶岩光など）。emissiveFollowsColor=false の
   * ときのみ参照する。未指定は発光なし。
   */
  readonly emissiveHex?: number
  /**
   * 丸い形状（球/円柱）を原色（高彩度）でもグロー光膜を付けて光らせるか。
   * Neon 専用（教材「ボールをネオンにすると光り輝く」）。ForceField も
   * emissiveFollowsColor=true だが半透明シマーで光膜は不自然なため対象外。
   */
  readonly saturatedRoundGlow?: boolean
}
// metalness を上げすぎると環境マップが無い本レンダラでは diffuse が消えて
// 黒く沈む（RJF02 すべり台が黒板状に見えた）。金属系は低めの値で金属感を
// 出しつつ、Part 色が保たれるようにする。
export const MATERIAL_SPECS: Record<RobloxMaterial, MaterialSpec> = {
  Plastic: { roughness: 0.5, metalness: 0, emissiveFollowsColor: false },
  SmoothPlastic: { roughness: 0.15, metalness: 0, emissiveFollowsColor: false },
  Neon: {
    roughness: 0.9,
    metalness: 0,
    emissiveFollowsColor: true,
    saturatedRoundGlow: true,
  },
  Wood: { roughness: 0.9, metalness: 0, emissiveFollowsColor: false },
  WoodPlanks: { roughness: 0.9, metalness: 0, emissiveFollowsColor: false },
  Marble: { roughness: 0.3, metalness: 0, emissiveFollowsColor: false },
  Basalt: { roughness: 0.95, metalness: 0, emissiveFollowsColor: false },
  Slate: { roughness: 0.95, metalness: 0, emissiveFollowsColor: false },
  CrackedLava: {
    roughness: 0.9,
    metalness: 0,
    emissiveFollowsColor: false,
    // 亀裂から覗く溶岩の鈍い光（Part 色に依らない固定色）。
    emissiveHex: 0xff3300,
  },
  Concrete: { roughness: 0.95, metalness: 0, emissiveFollowsColor: false },
  Limestone: { roughness: 0.9, metalness: 0, emissiveFollowsColor: false },
  Granite: { roughness: 0.85, metalness: 0, emissiveFollowsColor: false },
  Pavement: { roughness: 0.95, metalness: 0, emissiveFollowsColor: false },
  Brick: { roughness: 0.9, metalness: 0, emissiveFollowsColor: false },
  Pebble: { roughness: 0.95, metalness: 0, emissiveFollowsColor: false },
  Cobblestone: { roughness: 0.95, metalness: 0, emissiveFollowsColor: false },
  Rock: { roughness: 1, metalness: 0, emissiveFollowsColor: false },
  Sandstone: { roughness: 0.95, metalness: 0, emissiveFollowsColor: false },
  CorrodedMetal: {
    roughness: 0.8,
    metalness: 0.15,
    emissiveFollowsColor: false,
  },
  DiamondPlate: {
    roughness: 0.4,
    metalness: 0.25,
    emissiveFollowsColor: false,
  },
  Foil: { roughness: 0.25, metalness: 0.3, emissiveFollowsColor: false },
  Metal: { roughness: 0.35, metalness: 0.2, emissiveFollowsColor: false },
  Grass: { roughness: 1, metalness: 0, emissiveFollowsColor: false },
  LeafyGrass: { roughness: 1, metalness: 0, emissiveFollowsColor: false },
  Sand: { roughness: 1, metalness: 0, emissiveFollowsColor: false },
  Fabric: { roughness: 1, metalness: 0, emissiveFollowsColor: false },
  Snow: { roughness: 0.9, metalness: 0, emissiveFollowsColor: false },
  Mud: { roughness: 0.7, metalness: 0, emissiveFollowsColor: false },
  Ground: { roughness: 1, metalness: 0, emissiveFollowsColor: false },
  Asphalt: { roughness: 1, metalness: 0, emissiveFollowsColor: false },
  Salt: { roughness: 0.85, metalness: 0, emissiveFollowsColor: false },
  Ice: { roughness: 0.1, metalness: 0, emissiveFollowsColor: false },
  Glacier: { roughness: 0.2, metalness: 0, emissiveFollowsColor: false },
  Glass: { roughness: 0.05, metalness: 0, emissiveFollowsColor: false },
  ForceField: {
    roughness: 0.3,
    metalness: 0,
    emissiveFollowsColor: true,
  },
  Air: { roughness: 0.5, metalness: 0, emissiveFollowsColor: false },
  Water: { roughness: 0.1, metalness: 0, emissiveFollowsColor: false },
  Cardboard: { roughness: 0.95, metalness: 0, emissiveFollowsColor: false },
  Carpet: { roughness: 1, metalness: 0, emissiveFollowsColor: false },
  CeramicTiles: { roughness: 0.25, metalness: 0, emissiveFollowsColor: false },
  ClayRoofTiles: { roughness: 0.8, metalness: 0, emissiveFollowsColor: false },
  RoofShingles: { roughness: 0.95, metalness: 0, emissiveFollowsColor: false },
  Leather: { roughness: 0.7, metalness: 0, emissiveFollowsColor: false },
  Plaster: { roughness: 0.95, metalness: 0, emissiveFollowsColor: false },
  Rubber: { roughness: 1, metalness: 0, emissiveFollowsColor: false },
}

/**
 * 箱の上面に貼るスタッド（凸ボタン）の procedurally 生成テクスチャを返す。
 * 白背景 + 中央に薄暗い円で凹凸の見え方を表現。各 entry が自前のインスタンスを持ち、
 * repeat はサイズに応じて個別に更新する（共有不可）。
 */
export const createStudTexture = (): THREE.CanvasTexture => {
  const canvas = document.createElement('canvas')
  canvas.width = STUD_TEXTURE_SIZE
  canvas.height = STUD_TEXTURE_SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    throw new Error('Failed to acquire 2D context for stud texture')
  }
  // 白背景（タイルのベース色）。
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, STUD_TEXTURE_SIZE, STUD_TEXTURE_SIZE)
  // 中央の薄暗い円（スタッド天面の陰影風）。
  const center = STUD_TEXTURE_SIZE / 2
  const radius = STUD_TEXTURE_SIZE * 0.28
  const gradient = ctx.createRadialGradient(
    center,
    center,
    radius * 0.2,
    center,
    center,
    radius,
  )
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0.05)')
  gradient.addColorStop(0.7, 'rgba(0, 0, 0, 0.18)')
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0)')
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.arc(center, center, radius, 0, Math.PI * 2)
  ctx.fill()
  const texture = new THREE.CanvasTexture(canvas)
  texture.wrapS = THREE.RepeatWrapping
  texture.wrapT = THREE.RepeatWrapping
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

/**
 * Neon グローシェルの層構成。外側ほど薄い光膜を重ね、bloom の滲みに似せる
 * （単層の大きなシェルは「透明な枠」に見えてしまう）。
 */
export const GLOW_SHELL_LAYERS: ReadonlyArray<{
  scale: number
  opacity: number
}> = [
  { scale: 1.05, opacity: 0.28 },
  { scale: 1.12, opacity: 0.14 },
  { scale: 1.2, opacity: 0.07 },
]
/**
 * グローシェルを付ける彩度（max-min）の上限。白系 Neon（例: RJF08 のゴール
 * 鳥居）だけを対象にする。原色 Neon のブロック・ボールまで光膜を重ねると
 * 「ほとんどのブロックに透明な枠が付いた」ように見えてしまうため。
 */
export const GLOW_MAX_SATURATION = 0.25

/**
 * 天面用の模様テクスチャの repeat を天面サイズ（size.x/z, studs）に合わせる。
 * 側面用（applyState の materialTexture）は (x+z)/2 × y 基準なので別計算。
 */
export const applyTopPatternRepeat = (
  texture: THREE.Texture,
  state: { size: Vec3 },
): void => {
  texture.repeat.set(
    Math.max(0.5, state.size.x / MATERIAL_TILE_STUDS),
    Math.max(0.5, state.size.z / MATERIAL_TILE_STUDS),
  )
  texture.needsUpdate = true
}

/**
 * InstancedMesh 用の MeshStandardMaterial に、上面（+Y 面）だけスタッドを重ねる
 * シェーダ改変を施す。個別メッシュは上面マテリアルの texture.repeat を size で
 * 変えてスタッド数を出していたが、インスタンスは単一マテリアル共有のため、各面の
 * UV をインスタンス行列の XZ スケールで作り、studs/unit でタイルさせる。
 *
 * このマテリアルは InstancedMesh 専用（instanceMatrix が常に存在する）前提。
 */
export const applyInstancedStudShader = (
  material: THREE.MeshStandardMaterial,
  studTexture: THREE.Texture,
): void => {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.studMap = { value: studTexture }
    shader.vertexShader =
      'varying vec2 vStudUv;\nvarying float vTopFace;\n' +
      shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        // 上面（法線 +Y）判定と、インスタンス XZ スケールに比例した studs UV。
        vTopFace = step(0.5, normal.y);
        vStudUv = vec2(
          (position.x + 0.5) * length(instanceMatrix[0].xyz),
          (position.z + 0.5) * length(instanceMatrix[2].xyz)
        );`,
      )
    shader.fragmentShader =
      'uniform sampler2D studMap;\nvarying vec2 vStudUv;\nvarying float vTopFace;\n' +
      shader.fragmentShader.replace(
        '#include <map_fragment>',
        `#include <map_fragment>
        if (vTopFace > 0.5) {
          // RepeatWrapping のスタッドテクスチャ（白地＋陰の円）を上面に乗せる。
          vec3 stud = texture2D(studMap, vStudUv).rgb;
          diffuseColor.rgb *= stud;
        }`,
      )
  }
  // 改変済みプログラムを素の MeshStandardMaterial と別キャッシュにする。
  material.customProgramCacheKey = (): string => 'instanced-stud-v1'
}

/**
 * InstancedMesh 用の MeshStandardMaterial に、マテリアルの模様テクスチャ
 * （materialTextures の白ベース模様）を全面へタイルするシェーダ改変を施す。
 * 個別メッシュは applyState が texture.repeat をサイズから設定するが、
 * インスタンスは単一マテリアル共有のため、各面の UV を面法線の主軸で
 * 平面投影し、インスタンス行列のスケール（studs）に比例してタイルさせる。
 * 模様は map × color 乗算と同じく diffuse へ乗算され、Part 色で着色される。
 *
 * このマテリアルは InstancedMesh 専用（instanceMatrix が常に存在する）前提。
 */
export const applyInstancedPatternShader = (
  material: THREE.MeshStandardMaterial,
  patternTexture: THREE.Texture,
  cacheKey: string,
): void => {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.patternMap = { value: patternTexture }
    shader.vertexShader =
      'varying vec2 vPatternUv;\n' +
      shader.vertexShader.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        {
          // 面法線の主軸で平面投影し、インスタンスのスケールで studs 単位へ。
          vec3 instScale = vec3(
            length(instanceMatrix[0].xyz),
            length(instanceMatrix[1].xyz),
            length(instanceMatrix[2].xyz)
          );
          vec3 absNormal = abs(normal);
          vec2 patternStuds;
          if (absNormal.y >= absNormal.x && absNormal.y >= absNormal.z) {
            patternStuds = vec2(
              (position.x + 0.5) * instScale.x,
              (position.z + 0.5) * instScale.z
            );
          } else if (absNormal.x >= absNormal.z) {
            patternStuds = vec2(
              (position.z + 0.5) * instScale.z,
              (position.y + 0.5) * instScale.y
            );
          } else {
            patternStuds = vec2(
              (position.x + 0.5) * instScale.x,
              (position.y + 0.5) * instScale.y
            );
          }
          vPatternUv = patternStuds / ${MATERIAL_TILE_STUDS.toFixed(1)};
        }`,
      )
    shader.fragmentShader =
      'uniform sampler2D patternMap;\nvarying vec2 vPatternUv;\n' +
      shader.fragmentShader.replace(
        '#include <map_fragment>',
        `#include <map_fragment>
        diffuseColor.rgb *= texture2D(patternMap, vPatternUv).rgb;`,
      )
  }
  // 模様はマテリアルごとに異なるため、キャッシュキーも分ける。
  material.customProgramCacheKey = (): string => cacheKey
}
